import * as R from 'ramda';
import React from 'react';
import { arrayOf, func } from 'prop-types';
import { defineMessages, FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Link } from 'found';

import Table, {
  TableRow,
  TableCell,
  TableActionCell,
  TableHead,
  SELECTION_MULTIPLE,
} from 'common/mdc/table';

import { NodeLink } from '@sma/components/node';
import JobMenu from './menu';
import JobShape from './shape';
import DateTime from "common/components/dateTime";

export const messages = defineMessages({
  
  jobName: {
    id: 'table.header.jobName',
    defaultMessage: 'Name',
  },
  status: {
    id: 'table.header.status',
    defaultMessage: 'Status',
  },
  jobType: {
    id: 'table.header.jobType',
    defaultMessage: 'Type',
  },
  createdTime: {
    id: 'table.header.createdTime',
    defaultMessage: 'Created',
  },
  scheduledTime: {
    id: 'table.header.scheduledTime',
    defaultMessage: 'Scheduled',
  },
  updatedTime: {
    id: 'table.header.updatedTime',
    defaultMessage: 'Updated',
  },
  actions: {
    id: 'table.header.actions',
    defaultMessage: 'Actions',
  },
});

export const COLUMN_NAME = Symbol('jobName');
export const COLUMN_STATUS = Symbol('status');
export const COLUMN_TYPE = Symbol('jobType');
export const COLUMN_CREATED = Symbol('createdTime');
export const COLUMN_SCHEDULED = Symbol('scheduledTime');
export const COLUMN_UPDATED = Symbol('updatedTime');
export const COLUMN_ACTIONS = Symbol('actions');

export const columns = [
  COLUMN_NAME,
  COLUMN_STATUS,
  COLUMN_TYPE,
  COLUMN_CREATED,
  COLUMN_SCHEDULED,
  COLUMN_ACTIONS,
  COLUMN_UPDATED,
];

export const jobHead = ({ isSorted, toggleSort, displayColumns = columns, intl, ...props }) => {
  const hidden = R.complement(R.flip(R.contains)(displayColumns));
  return (
    <TableHead {...props}>
      <TableCell
        sortable
        columnId={COLUMN_NAME}
        title={intl.formatMessage(messages.jobName)}
        hidden={hidden(COLUMN_NAME)}
        onClick={() => toggleSort('jobName')}
        sorted={isSorted('jobName')}
      ><FormattedMessage {...messages.jobName} />
      </TableCell>
      <TableCell
        sortable
        columnId={COLUMN_STATUS}
        title={intl.formatMessage(messages.status)}
        hidden={hidden(COLUMN_STATUS)}
        onClick={() => toggleSort('status')}
        sorted={isSorted('status')}
      ><FormattedMessage {...messages.status} />
      </TableCell>
      <TableCell
        sortable
        columnId={COLUMN_TYPE}
        title={intl.formatMessage(messages.jobType)}
        hidden={hidden(COLUMN_TYPE)}
        onClick={() => toggleSort('jobType')}
        sorted={isSorted('jobType')}
        style={{
          minWidth: '80px',
        }}
      ><FormattedMessage {...messages.jobType} />
      </TableCell>
      <TableCell
        sortable
        columnId={COLUMN_CREATED}
        title={intl.formatMessage(messages.createdTime)}
        hidden={hidden(COLUMN_CREATED)}
        onClick={() => toggleSort('createdTime')}
        sorted={isSorted('createdTime')}
      ><FormattedMessage {...messages.createdTime} />
      </TableCell>
      <TableCell
        sortable
        columnId={COLUMN_SCHEDULED}
        title={intl.formatMessage(messages.scheduledTime)}
        hidden={hidden(COLUMN_SCHEDULED)}
        onClick={() => toggleSort('scheduledTime')}
        sorted={isSorted('scheduledTime')}
      ><FormattedMessage {...messages.scheduledTime} />
      </TableCell>
      <TableCell
        sortable
        columnId={COLUMN_UPDATED}
        title={intl.formatMessage(messages.updatedTime)}
        onClick={() => toggleSort('updatedTime')}
        sorted={isSorted('updatedTime')}
      ><FormattedMessage {...messages.updatedTime} />
      </TableCell>
      <TableCell
        actions
        columnId={COLUMN_ACTIONS}
        title={intl.formatMessage(messages.actions)}
        hidden={hidden(COLUMN_ACTIONS)}
      ><FormattedMessage {...messages.actions} />
      </TableCell>
    </TableHead>
  );
};

export const jobRow = props => job => (
  <TableRow
    {...props}
  >
    <TableCell columnId={COLUMN_NAME}>{job.jobName}</TableCell>
    <TableCell
      columnId={COLUMN_STATUS}
    >
      {job.status}
    </TableCell>
    <TableCell
      columnId={COLUMN_TYPE}
    >
      {job.jobType}
    </TableCell>
    <TableCell columnId={COLUMN_CREATED}>
      {
        job.createdTime ? <DateTime value={job.createdTime} seconds /> : null
      }
    </TableCell>
    <TableCell columnId={COLUMN_SCHEDULED}>
      {
        job.scheduledTime ? <DateTime value={job.scheduledTime} seconds /> : null
      }
    </TableCell>
    <TableCell columnId={COLUMN_UPDATED}>
      {
        job.updatedTime ? <DateTime value={job.updatedTime} seconds /> : null
      }
    </TableCell>
    <TableActionCell columnId={COLUMN_ACTIONS}>
      <JobMenu job={job} />
    </TableActionCell>
  </TableRow>
);

const JobTable = ({
  jobs,
  isSorted,
  toggleSort,
  isSelected,
  onSelectionChange,
  displayColumns,
  intl,
  ...props
}) => (
  <Table
    className="meters-table"
    {...props}
  >
    {jobHead({
      isSorted,
      toggleSort,
      displayColumns,
      intl,
    })}
    {jobs.map(job => jobRow({
      selected: isSelected(job),
      onClick: () => onSelectionChange(job),
    })(job))}
  </Table>
);

export const JobSelectionTable = injectIntl(({
  displayColumns,
  jobs,
  isSorted,
  toggleSort,
  isSelected,
  onSelect,
  isAllSelected,
  onSelectionChange,
  intl,
  ...props
}) => (
  <Table
    className="meters-table"
    selectable
    selectionType={SELECTION_MULTIPLE}
    {...props}
  >
    {jobHead({
      intl,
      displayColumns,
      toggleSort,
      isSorted,
      onSelectionChange,
      checked: isAllSelected,
    })}
    {jobs.map(job => jobRow({
      checked: isSelected(job),
      onSelect: () => onSelect(job),
      onClick: () => onSelect(job),
    })(job))}
  </Table>
));

JobTable.propTypes = {
  jobs: arrayOf(JobShape).isRequired,
  toggleSort: func.isRequired,
  isSorted: func.isRequired,
  isSelected: func,
  onSelectionChange: func,
  intl: intlShape.isRequired,
};

JobTable.defaultProps = {
  isSelected: () => false,
  onSelectionChange: () => {},
};

export default injectIntl(JobTable);
